import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/AxiosInstance";

const toBool = (v) => Number(v) === 1 || v === true;

// ✅ GET /api/admin/users
export const fetchAdminUsersThunk = createAsyncThunk(
  "adminPerm/fetchUsers",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/admin/users");
      return res.data?.users || [];
    } catch (e) {
      return rejectWithValue(
        e.response?.data?.message || e.message || "Failed to load users"
      );
    }
  }
);

// ✅ GET /api/admin/user-permissions/:userId
export const fetchUserPermissionsThunk = createAsyncThunk(
  "adminPerm/fetchUserPermissions",
  async (userId, { rejectWithValue }) => {
    try {
      const res = await api.get(`/api/admin/user-permissions/${userId}`);
      const list = res.data?.permissions || [];
      return list.map((r) => ({
        module_key: r.module_key,
        module_name: r.module_name,
        can_view: toBool(r.can_view),
        can_create: toBool(r.can_create),
        can_update: toBool(r.can_update),
        can_delete: toBool(r.can_delete),
      }));
    } catch (e) {
      return rejectWithValue(
        e.response?.data?.message || e.message || "Failed to load permissions"
      );
    }
  }
);

// ✅ POST /api/admin/user-permissions
export const saveUserPermissionsThunk = createAsyncThunk(
  "adminPerm/saveUserPermissions",
  async ({ user_id, rows }, { rejectWithValue }) => {
    try {
      const payload = {
        user_id: Number(user_id),
        permissions: (rows || []).map((r) => ({
          module_key: r.module_key,
          can_view: r.can_view ? 1 : 0,
          can_create: r.can_create ? 1 : 0,
          can_update: r.can_update ? 1 : 0,
          can_delete: r.can_delete ? 1 : 0,
        })),
      };
      const res = await api.post("/api/admin/user-permissions", payload);

      if (!res.data?.success) {
        return rejectWithValue(res.data?.message || "Save failed");
      }
      return true;
    } catch (e) {
      return rejectWithValue(
        e.response?.data?.message || e.message || "Failed to save permissions"
      );
    }
  }
);

const adminPermissionSlice = createSlice({
  name: "adminPerm",
  initialState: {
    users: [],
    selectedUserId: "",
    rows: [],

    loadingUsers: false,
    loadingPerms: false,
    saving: false,
    error: null,
  },
  reducers: {
    setSelectedUserId(state, action) {
      state.selectedUserId = String(action.payload || "");
    },

    // local UI updates (no API)
    updateCell(state, action) {
      const { moduleKey, field, value } = action.payload;
      state.rows = (state.rows || []).map((r) =>
        r.module_key === moduleKey ? { ...r, [field]: value } : r
      );
    },

    toggleRowAll(state, action) {
      const { moduleKey, value } = action.payload;
      state.rows = (state.rows || []).map((r) =>
        r.module_key === moduleKey
          ? {
              ...r,
              can_view: value,
              can_create: value,
              can_update: value,
              can_delete: value,
            }
          : r
      );
    },

    toggleColumnAll(state, action) {
      const { field, value } = action.payload;
      state.rows = (state.rows || []).map((r) => ({ ...r, [field]: value }));
    },

    clearAdminPermState(state) {
      state.users = [];
      state.selectedUserId = "";
      state.rows = [];
      state.loadingUsers = false;
      state.loadingPerms = false;
      state.saving = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // users
      .addCase(fetchAdminUsersThunk.pending, (state) => {
        state.loadingUsers = true;
        state.error = null;
      })
      .addCase(fetchAdminUsersThunk.fulfilled, (state, action) => {
        state.loadingUsers = false;
        state.users = action.payload || [];
      })
      .addCase(fetchAdminUsersThunk.rejected, (state, action) => {
        state.loadingUsers = false;
        state.users = [];
        state.error = action.payload || "Failed to load users";
      })

      // permissions
      .addCase(fetchUserPermissionsThunk.pending, (state) => {
        state.loadingPerms = true;
        state.error = null;
      })
      .addCase(fetchUserPermissionsThunk.fulfilled, (state, action) => {
        state.loadingPerms = false;
        state.rows = action.payload || [];
      })
      .addCase(fetchUserPermissionsThunk.rejected, (state, action) => {
        state.loadingPerms = false;
        state.rows = [];
        state.error = action.payload || "Failed to load permissions";
      })

      // save
      .addCase(saveUserPermissionsThunk.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveUserPermissionsThunk.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(saveUserPermissionsThunk.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload || "Failed to save permissions";
      });
  },
});

export const {
  setSelectedUserId,
  updateCell,
  toggleRowAll,
  toggleColumnAll,
  clearAdminPermState,
} = adminPermissionSlice.actions;

export default adminPermissionSlice.reducer;
